import {
  cancelBookingPaymentAttempts,
  getBookingPaymentStatus,
} from '@/features/purchase/purchaseService';
import { getAdyenLocale } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import '@/styles/adyen-override.css';
import type { PayWithAdyenResponseDTO } from '@/types/dto/agent';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import {
  AdyenCheckout,
  Dropin,
  type CoreConfiguration,
} from '@adyen/adyen-web/auto';
import '@adyen/adyen-web/styles/adyen.css';
import { Dialog, ScrollArea, Typography } from '@fleet/webshop-ui';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { toast } from 'react-toastify';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
  paymentSession: PayWithAdyenResponseDTO | null;
  bookingId?: string;
  bookingAmount?: number;
  bookingCurrency?: string;
  isRedirectReturn?: boolean;
}

const PaymentModal: FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onPaymentSuccess,
  paymentSession,
  bookingId,
  bookingAmount,
  bookingCurrency,
  isRedirectReturn = false,
}) => {
  const dropinRef = useRef<HTMLDivElement>(null);
  const dropinInstance = useRef<Dropin | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const paymentSuccessRef = useRef(false);

  const handleClose = useCallback(async () => {
    if (!isProcessing && bookingId && !paymentSuccessRef.current) {
      try {
        await cancelBookingPaymentAttempts(bookingId);
      } catch (error) {
        console.error('Error canceling payment attempt:', error);
      }
    }
    if (!isProcessing) {
      onClose();
    }
  }, [isProcessing, bookingId, onClose]);

  const startPollingPaymentStatus = useCallback(async () => {
    if (!bookingId) return;

    const checkStatus = async () => {
      try {
        const status = await getBookingPaymentStatus(bookingId);

        if (status === 'paid') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          paymentSuccessRef.current = true;
          setIsProcessing(false);
          trackCustomEvent(GTM_EVENT.ADD_PAYMENT_INFO, {
            currency: bookingCurrency,
            value: bookingAmount,
          });
          onPaymentSuccess();
        } else if (status === 'not_paid') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          setIsProcessing(false);
          toast.error(<TransAlert i18nKey="paymentFailed" />);
          onClose();
        }
      } catch (error) {
        toast.error(<TransAlert i18nKey="paymentFailed" />);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        setIsProcessing(false);
        onClose();
      }
    };

    setIsProcessing(true);
    if (dropinRef.current) {
      dropinRef.current.style.display = 'none';
    }
    await checkStatus();
    pollingIntervalRef.current = setInterval(checkStatus, 2000);
  }, [bookingId, onPaymentSuccess, onClose, bookingCurrency, bookingAmount]);

  const loadAdyen = useCallback(async () => {
    try {
      if (!paymentSession?.session || !paymentSession?.clientKey) {
        console.error('Missing payment session data');
        return;
      }

      const options: CoreConfiguration = {
        locale: getAdyenLocale(),
        clientKey: paymentSession.clientKey,
        environment: paymentSession.isTestEnvironment ? 'test' : 'live',
        session: paymentSession.session,
        translations: {
          'de-DE': {
            payButton: 'Bezahlen',
          },
        },
        onError() {
          toast.error(<TransAlert i18nKey="paymentFailed" />);
          onClose();
        },
        onPaymentCompleted() {
          void startPollingPaymentStatus();
        },
        onPaymentFailed() {
          toast.error(<TransAlert i18nKey="paymentFailed" />);
          onClose();
        },
      };

      const checkout = await AdyenCheckout(options);

      if (dropinRef.current) {
        const dropin = new Dropin(checkout);
        dropinInstance.current = dropin;
        await dropin.mount(dropinRef.current);
      } else {
        console.error('Dropin ref is not available');
      }
    } catch (error) {
      console.error('Error initializing Adyen:', error);
    }
  }, [
    onClose,
    paymentSession?.clientKey,
    paymentSession?.isTestEnvironment,
    paymentSession?.session,
    startPollingPaymentStatus,
  ]);

  useEffect(() => {
    if (isOpen) {
      paymentSuccessRef.current = false;
      if (isRedirectReturn) {
        void startPollingPaymentStatus();
      } else if (paymentSession) {
        void loadAdyen();
      }
    }

    return () => {
      if (dropinInstance.current) {
        dropinInstance.current.unmount();
        dropinInstance.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [
    isOpen,
    loadAdyen,
    paymentSession,
    isRedirectReturn,
    startPollingPaymentStatus,
  ]);

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={() => handleClose()}
      isDismissable={!isProcessing}
      isKeyboardDismissDisabled={isProcessing}
      hideCloseButton={isProcessing}
      headerTitle={<TransText i18nKey="payment" />}
      className={{ content: 'p-0' }}
      data-testid="payment-modal"
    >
      <ScrollArea className="h-[calc(600px)]">
        <div className="relative min-h-[550px] p-6">
          {!isRedirectReturn && <div ref={dropinRef} id="dropin" />}
          {(isProcessing || isRedirectReturn) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <Typography
                variant="paragraph"
                className="mt-4 text-center text-dark"
              >
                <TransText i18nKey="yourPaymentIsBeingProcessedPleaseWait" />
              </Typography>
            </div>
          )}
        </div>
      </ScrollArea>
    </Dialog>
  );
};

export default PaymentModal;
